/* eslint-disable react/no-danger */
import React, { useEffect, useState } from 'react';
import { clone, cloneDeep, isEmpty, mapValues } from 'lodash';
import { Link } from 'gatsby';
import Form from '../../services/Form';
import Button from '../layouts/Button';
import ContactFormTel from './ContactFormTel';
import ContactFormEmail from './ContactFormEmail';
import ContactFormText from './ContactFormText';
import ContactFormTextarea from './ContactFormTextarea';
import ContactFormSelect from './ContactFormSelect';
import ContactFormSelectMultiple from './ContactFormSelectMultiple';
import ContactFormSelectFlags from './ContactFormSelectFlags';
import countries from '../../assets/data/countries.json';
import FormRecaptcha from './FormRecaptcha';
import Icons from '../layouts/Icons';

export default function ContactUsForm({ data }) {
  const { formId, policyText, buttonText, title, text, cardsList } = data;
  const [formValues, setFormValues] = useState([]);
  const [inputValues, setInputValues] = useState({});
  const [disableSubmit, setDisableSubmit] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [submitted, setSubmitted] = useState({ message: '', status: false });
  const [selectedCountry, setSelectedCountry] = useState(countries[0]);

  const [captchaValue, setCaptchaValue] = useState('');

  const handleCaptchaChange = (value) => {
    setCaptchaValue(value);
  };

  useEffect(() => {
    const form = new Form(formId);
    form
      .get()
      .then((res) => {
        setFormValues(res.pages[0].fieldsets[0].columns[0].fields);
        setSubmitted({ ...submitted, message: res.messageOnSubmit });
      })
      .catch((err) => console.log(err));
  }, []);

  const handleInitReset = () => {
    if (!isEmpty(formValues)) {
      const values = {};
      formValues.forEach((item) => {
        values[item.alias] = {
          name: item.alias,
          value: '',
          validation: false,
          required: item.required,
        };
      });
      setInputValues(values);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    handleInitReset();
  }, [formValues]);

  useEffect(() => {
    const values = Object.values(inputValues);
    const filtered = values.reduce((acc, curr) => {
      if (!curr.validation) acc.push(curr);
      return acc;
    }, []);

    if (filtered.length > 0) setDisableSubmit(true);
    else setDisableSubmit(false);
  }, [inputValues]);

  useEffect(() => {
    setInputValues((prevValues) => ({
      ...prevValues,
      recaptcha: { value: captchaValue, validation: !!captchaValue },
    }));
  }, [captchaValue]);

  const validateFormValues = (value, required, pattern) => {
    if (!required && !value) return true;

    if (Array.isArray(value)) {
      if (!required) return true;
      return value.length > 0;
    }

    if (typeof value === 'string' && pattern) {
      const regex = new RegExp(pattern);
      return regex.test(value);
    }

    return typeof value === 'string' && value.trim().length > 0;
  };

  const handleChange = (event, pattern = false, isMultiField = false) => {
    if (isMultiField) {
      event.forEach((item) => {
        const { name, value, required } = item;
        setInputValues((prev) => {
          const currValues = { ...prev };
          currValues[name] = {
            name,
            value,
            validation: !!validateFormValues(value, required, pattern),
            required,
          };
          return currValues;
        });
      });
    } else {
      const { name, value, required } = event.target;
      setInputValues((prev) => {
        const currValues = { ...prev };
        currValues[name] = {
          name,
          value,
          validation: !!validateFormValues(value, required, pattern),
          required,
        };
        return currValues;
      });
    }
  };

  const handleErrorText = (field) => {
    if (field.value) return field.validation;
    return true;
  };

  const handleSubmit = () => {
    const form = new Form(formId);
    const currValues = cloneDeep(inputValues);
    currValues.phoneNumber.value = `${selectedCountry.code}${currValues.phoneNumber.value}`;
    const values = mapValues(currValues, 'value');
    values.dataConsent = 'on';
    setIsLoading(true);

    form
      .post(values)
      .then((res) => {
        // console.log(res);
        const val = {};
        formValues.forEach((item) => {
          val[item.alias] = { value: '', validation: false, required: item.required };
        });
        setInputValues(val);
        setSubmitted({ ...submitted, status: true });
      })
      .catch((err) => console.log(err));
  };

  const renderLoading = () => {
    if (submitted.status)
      return (
        <div className="w-full absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-2/3">
          <span
            className="flex text-[7vw] xs:text-[18px] w-full xs:min-w-[350px] lg:text-center
          font-roboto-condensed-bold uppercase mx-auto xl:text-[22px] pt-[7rem]
          text-left success-message"
            id="success-message">
            {submitted.message}
          </span>
        </div>
      );
    return (
      <div className="flex items-center justify-center">
        <div
          className="border-t-bg-6 ease-linear rounded-full border-8 border-t-8 h-48
            w-48 g-4 animate-spinner"
        />
      </div>
    );
  };
  return (
    <div className={`${text.includes('Summer League') ? 'max-w-[934px]' : 'lg:w-[67%]'}`}>
      <div className="lg:pb-[43px] pb-[14px]">
        <h2
          className="uppercase text-primary font-roboto-condensed-bold lg:text-2xl text-[35px]
      leading-[45px] mb-4">
          {title === '' ? 'CONTACT US' : title}
        </h2>

        {text.includes('Summer League')
          ? isEmpty(cardsList) || (
              <div className="grid md:grid-cols-2 gap-14 pt-4 pb-10">
                {cardsList.map((card, index) => (
                  <div 
                  className={`flex gap-5 ${index === 0 ? 'md:max-w-[400px]' : ''}`} key={index}>
                    <div className="min-w-[40px] md:min-w-[64px]">
                      <Icons 
                      icon={`${index === 0 ? 'phone' : 'email'}`} 
                      className="w-[40px] md:w-[64px]" />
                    </div>
                    <div className='flex flex-col space-y-5'>
                    <h3
                      className="uppercase font-roboto-condensed-bold text-[23px]
                text-bg-3">
                      {card.content.title}
                    </h3>
                    <div
                      className="font-roboto-regular"
                      dangerouslySetInnerHTML={{ __html: card.content.infoText }}
                    />
                    {index === 0 ? (
                      <Link
                        to="tel:+97143064347"
                        className="bg-red hover:shadow-sm hover:bg-white 
                    hover:text-black text-xs text-white uppercase rounded-sm font-roboto-bold 
                    border border-red  flex cursor-pointer items-center justify-center 
                    xl:w-[170px] w-auto lg2:w-[150px]  min-h-[42px] px-6 py-2 text-center 
                    font-bold transition duration-300 ease-in-out max-w-[170px]">
                        <span className="hidden md:inline">+971 4 306-4347</span>
                        <span className="md:hidden">Call Us</span>
                      </Link>
                    ) : (
                      <div>
                      <Link
                        to="mailto:alexine.baesa@dwtc.com"
                        className="bg-red hover:shadow-sm hover:bg-white 
                    hover:text-black text-xs text-white uppercase rounded-sm font-roboto-bold 
                    border border-red  inline-flex cursor-pointer items-center justify-center 
                    min-w-[170px] w-auto lg2:min-w-[150px] min-h-[42px] px-6 py-2 text-center 
                    font-bold transition duration-300 ease-in-out">
                        <span className="hidden md:inline">alexine.baesa@dwtc.com</span>
                        <span className="md:hidden">Email Us</span>
                      </Link>
                      </div>
                    )}
                    </div>
                  </div>
                ))}
              </div>
            )
          : ''}

        {isEmpty(text) || (
          <p
            className="mb-5 [&>a]:text-[#E11E37] [&>a]:underline 
            hover:[&>a]:no-underline font-roboto-regular"
            dangerouslySetInnerHTML={{ __html: text }}
          />
        )}
      </div>
      <div
        className={`
          ${text.includes('Summer League') ? 'max-w-[770px]' : 'w-full'} 
          h-auto  relative
        `}>
        {isLoading ? (
          renderLoading()
        ) : (
          <div
            className="w-full h-auto flex flex-col items-center justify-start gap-y-10
              md:flex-row md:flex-wrap md:gap-x-[30px]">
            {formValues.map((item) => {
              const { settings, alias, required, type, preValues, requiredErrorMessage } = item;
              const {
                fieldType,
                placeholder,
                pattern,
                patternInvalidErrorMessage: errMsg,
                maximumLength,
                defaultValue,
              } = settings;

              if (type === 'text') {
                switch (fieldType) {
                  case 'tel':
                    return (
                      <div className="w-full flex flex-nowrap md:w-[calc(50%-15px)]">
                        <ContactFormSelectFlags
                          selectedCountry={selectedCountry}
                          countries={countries}
                          setSelectedCountry={setSelectedCountry}
                        />
                        <ContactFormTel
                          alias={alias}
                          required={required}
                          inputValues={inputValues}
                          handleChange={handleChange}
                          placeholder={placeholder}
                          pattern={pattern}
                          errMsg={errMsg}
                          handleErrorText={handleErrorText}
                          selectedCountry={selectedCountry}
                        />
                      </div>
                    );
                  case 'email':
                    return (
                      <div className="w-full md:w-[calc(50%-15px)]">
                        <ContactFormEmail
                          alias={alias}
                          required={required}
                          inputValues={inputValues}
                          handleChange={handleChange}
                          placeholder={placeholder}
                          pattern={pattern}
                          errMsg={errMsg}
                          handleErrorText={handleErrorText}
                        />
                      </div>
                    );
                  default:
                    return (
                      <div className="w-full md:w-[calc(50%-15px)]">
                        <ContactFormText
                          alias={alias}
                          required={required}
                          inputValues={inputValues}
                          handleChange={handleChange}
                          placeholder={placeholder}
                          pattern={pattern}
                          errMsg={errMsg}
                          handleErrorText={handleErrorText}
                        />
                      </div>
                    );
                }
              }
              if (type === 'textarea') {
                return (
                  <ContactFormTextarea
                    alias={alias}
                    required={required}
                    inputValues={inputValues}
                    handleChange={handleChange}
                    placeholder={placeholder}
                    pattern={pattern}
                    errMsg={errMsg}
                    handleErrorText={handleErrorText}
                    maximumLength={maximumLength}
                  />
                );
              }
              if (type === 'select') {
                return alias === 'sportType' ? (
                  <div className="w-full relative md:w-[calc(50%-15px)]">
                    <ContactFormSelectMultiple
                      alias={alias}
                      hiddenField={formValues.find((value) => value.alias === `${alias}Label`)}
                      required={required}
                      inputValues={inputValues}
                      handleChange={handleChange}
                      defaultValue={defaultValue}
                      pattern={pattern}
                      errMsg={requiredErrorMessage}
                      handleErrorText={handleErrorText}
                      preValues={preValues}
                    />
                  </div>
                ) : (
                  <div className="w-full relative">
                    <div className="w-full relative md:w-[calc(50%-15px)]">
                      <ContactFormSelect
                        alias={alias}
                        hiddenField={formValues.find((value) => value.alias === `${alias}Label`)}
                        required={required}
                        inputValues={inputValues}
                        handleChange={handleChange}
                        defaultValue={defaultValue}
                        pattern={pattern}
                        errMsg={requiredErrorMessage}
                        handleErrorText={handleErrorText}
                        preValues={preValues}
                      />
                    </div>
                  </div>
                );
              }
              if (type === 'recaptcha2') {
                return (
                  <div className="w-full md:w-[calc(50%-15px)]">
                    <FormRecaptcha
                      alias={alias}
                      siteKey={process.env.GATSBY_RECAPTCHA_SITE_KEY}
                      onCaptchaChange={handleCaptchaChange}
                      inputValues={{ recaptcha: { value: captchaValue } }}
                      handleErrorText={(input) => !!input.value}
                    />
                  </div>
                );
              }

              return null;
            })}
            <div className="w-full flex flex-col gap-y-10">
              <p
                className="font-roboto-regular text-[15px] max-w-[525px] text-darkGrey 
              [&>a]:text-red [&>a]:underline hover:[&>a]:no-underline"
                dangerouslySetInnerHTML={{ __html: policyText }}
              />
              <Button
                label={buttonText}
                ariaLabel="submit button"
                variant="primaryRed"
                text="uppercase"
                w="w-fit min-w-[170px]"
                clickHandler={handleSubmit}
                tagName="submit button"
                isDisabled={disableSubmit}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
